import { Map as IMap } from 'immutable';
import { CRUX_APP_ERROR } from 'js/actions/errorHandler';
import * as ClapiAction from 'js/actions/clapi';
import { LOWERCASE_ALL, RESULT_VIEW } from 'js/constants/rapidSearch';
import {
    SET_DYNAMIC_COORDINATES,
    GET_PROPERTY_BY_COORDINATES_FAILED,
    GET_PROPERTY_BY_COORDINATES_SUCCESS, HANDLE_DYNAMIC_PROPERTY_SELECTED,
    SET_SELECTED_PROPERTY,
    SET_SELECTED_PROPERTY_FROM_DYNAMIC_BUBBLE
} from 'js/actions/search';
import AddressHelper from 'js/helpers/Address';
import Commons from 'js/helpers/Commons';
import { haveNonShapeFiltersChanged } from 'js/helpers/search/FilterComparisonHelper';

// ---------------------------------------------------------------------------
// Retained-snapshot helpers
// ---------------------------------------------------------------------------


/**
 * Extracts an ordered array of offsets from search metadata.
 * Handles both array-metadata (multi-shape batch response) and single-object metadata.
 * Returns [] when metadata is absent so callers can safely use .length / index access.
 */
const getOffsetsFromMetadata = (metadata) => {
    if (Array.isArray(metadata)) {
        return metadata.map((m) => (m && typeof m.offset === 'number' ? m.offset : 0));
    }
    if (metadata) {
        return [typeof metadata.offset === 'number' ? metadata.offset : 0];
    }
    return [];
};


/**
 * Merges a single pair of retained/current metadata entries and stamps the
 * correct per-source offset onto the result.
 */
const mergeMetaEntry = (retainedMeta, currentMeta, offset) => {
    const base = { ...(retainedMeta || {}), ...(currentMeta || {}) };
    if (typeof offset === 'number') base.offset = offset;
    return Object.keys(base).length ? base : undefined;
};

/**
 * Merges retained and current metadata (scalar or array) so that field values
 * like `total` and `totalPages` stay current while `offset` reflects the merged
 * per-source offsets.
 */
const mergeMetadata = (retainedMetadata, currentMetadata, sourceOffsets) => {
    if (Array.isArray(currentMetadata) || Array.isArray(retainedMetadata)) {
        const retainedArray = Array.isArray(retainedMetadata) ? retainedMetadata : [];
        const currentArray = Array.isArray(currentMetadata) ? currentMetadata : [];
        const maxLen = Math.max(retainedArray.length, currentArray.length);
        return Array.from({ length: maxLen }, (_, i) =>
            mergeMetaEntry(retainedArray[i], currentArray[i], sourceOffsets[i]),
        );
    }
    if (retainedMetadata || currentMetadata) {
        return mergeMetaEntry(retainedMetadata, currentMetadata, sourceOffsets[0]);
    }
    return currentMetadata;
};

/**
 * Builds the `retainedSearchResults` snapshot that is stored whenever a new
 * map shape fires.  Combines previous retained data with the current page,
 * strips explicitly-excluded properties, and computes merged per-source offsets.
 */
const buildRetainedSnapshot = (currentRetained, currentResults, newFilters) => {
    let combinedData = Commons.removeDuplicatePropertiesById([
        ...(currentRetained?.data || []),
        ...(currentResults?.data || []),
    ]);

    const excludedIdSet = new Set(newFilters.mapSearch?.excludedIds || []);
    if (excludedIdSet.size > 0) {
        combinedData = combinedData.filter(p => !excludedIdSet.has(p.id));
    }

    const retainedMetadata = currentRetained?.metadata;
    const currentMetadata = currentResults?.metadata;
    const previousOffsets =
        Array.isArray(currentRetained?.sourceOffsets) && currentRetained.sourceOffsets.length
            ? currentRetained.sourceOffsets
            : getOffsetsFromMetadata(retainedMetadata);
    const currentOffsets = getOffsetsFromMetadata(currentMetadata);
    const maxLength = Math.max(previousOffsets.length, currentOffsets.length);
    const sourceOffsets = Array.from({ length: maxLength }, (_, index) => {
        const retainedOffset = previousOffsets[index] ?? 0;
        const currentOffset = currentOffsets[index] ?? 0;
        return Math.max(retainedOffset, currentOffset);
    });

    return {
        data: combinedData,
        metadata: mergeMetadata(retainedMetadata, currentMetadata, sourceOffsets),
        sourceOffsets,
    };
};

/**
 * Filters `selectedProperties` down to entries whose property IDs appear in
 * `payload`.  Returns an empty object when `payload` is empty/absent.
 * Extracted from HANDLE_DYNAMIC_PROPERTY_SELECTED to reduce reducer nesting.
 */
const retainSelectedByPayload = (selectedProperties, payload) => {
    if (!payload?.length) return {};
    return Object.entries(selectedProperties)
        .reduce((acc, [fieldName, properties]) => {
            const existingProperties = properties
                .filter(item => payload.includes(item.id));
            if (existingProperties.length > 0 && !acc[fieldName]) {
                acc[fieldName] = properties;
            }
            return acc;
        }, {});
};

// ---------------------------------------------------------------------------

export const initialState = () => IMap({
    searchResults: null,
    retainedSearchResults: null, // snapshot of previous search results to retain displayed properties on map shape/add-remove
    searchTabSelected: LOWERCASE_ALL,
    propertySummaries: [],
    isFetchingAdditionalDetails: false,
    searchFilters: null, // searchFiltersV2
    statusType: LOWERCASE_ALL,
    limit: null,
    pageLimitOffset: null,
    // Per-source offsets for Show More in multi-source (map shape) searches.
    // Stored separately from searchFilters so they are NEVER forwarded to the backend
    // as filter fields by ApiHelper.buildRequest's unknown-key path.
    pagingSourceOffsets: null,
    activeView: null,
    displayControl: 'all', // persists All/Selected tab across view switches
    map: IMap({
        selectedProperty: null,
        dynamic: {
            coordinates: null,
            properties: null,
            selectedProperty: [],
        }
    }),
});

/**
 * Handles the SET_SEARCH_FILTERS_V2 / SET_SAVED_LIST_FILTERS action.
 * Extracted from the reducer to bring its cognitive complexity within limits.
 */
const handleSetSearchFiltersV2 = (state, payload) => {
    const { isNonAddressSearch, ...rest } = payload;
    const hasMapSearch = !!rest.mapSearch;
    const prevFilters = state.get('searchFilters') || {};
    const prevHadMapSearch = !!(prevFilters.mapSearch);
    const currentResults = state.get('searchResults');
    const currentRetained = state.get('retainedSearchResults');
    const hasCurrentData = !!(currentResults?.data?.length);
    const hasExistingRetained = !!(currentRetained?.data?.length);

    const mapReset = state.get('activeView') === RESULT_VIEW.MAP
        ? { map: initialState().get('map') }
        : {};

    if (hasMapSearch && prevHadMapSearch && haveNonShapeFiltersChanged(prevFilters, rest)) {
        return state.merge(IMap({
            searchFilters: { ...rest },
            searchResults: null,
            retainedSearchResults: null,
            ...mapReset,
        }));
    }

    if (hasMapSearch && rest.mapSearch?.isRemoveBoundary) {
        return state.merge(IMap({
            searchFilters: { ...rest },
            retainedSearchResults: null,
            ...mapReset,
        }));
    }

    const retainedSearchResults = hasMapSearch && (hasCurrentData || hasExistingRetained)
        ? buildRetainedSnapshot(currentRetained, currentResults, rest)
        : null;

    return state.merge(IMap({
        searchFilters: { ...rest },
        retainedSearchResults,
        pagingSourceOffsets: null,
        ...mapReset,
    }));
};

export default function reducer(state = initialState(), action = {}) {
    switch (action.type) {
        case ClapiAction.UPDATE_SEARCH_FILTERS_OFFSET: {
            const { isNonAddressSearch, sourceOffsets, ...rest } = action.payload;
            return state.merge(IMap({
                searchFilters: { ...state.get('searchFilters'), ...rest },
                // Keep sourceOffsets out of searchFilters — ApiHelper.buildRequest treats
                // unrecognised keys as equals-filters and would send them to the backend.
                pagingSourceOffsets: sourceOffsets ?? null,
            }));
        }
        case ClapiAction.SET_SAVED_LIST_FILTERS:
        case ClapiAction.SET_SEARCH_FILTERS_V2:
            return handleSetSearchFiltersV2(state, action.payload);
        case CRUX_APP_ERROR: case ClapiAction.CLEAR_SEARCH_FILTERS:
            // excludes resetting the `activeView` and `statusType`
            return initialState().merge(IMap({
                statusType: state.get('statusType'),
                activeView: state.get('activeView'),
            }));
        case ClapiAction.GET_SEARCH_RESULTS_SUCCESS:
        case ClapiAction.GET_SAVED_LIST_RESULT_SUCCESS:
        case ClapiAction.GET_RAPID_NAME_SEARCH_RESULT_SUCCESS:
            if (action.payload) {
                return state.merge(IMap({
                    searchResults: action.payload,
                }));
            }
            return state;
        case ClapiAction.CLEAR_SEARCH_RESULTS:
            return state.merge(IMap({
                searchResults: null,
                retainedSearchResults: null,
                pagingSourceOffsets: null,
                displayControl: 'all',
            }));
        case ClapiAction.UPDATE_SEARCH_RESULTS:
            return state.merge(IMap({
                searchResults: action.payload,
            }));
        case ClapiAction.SHOW_MORE_SUCCESS:
            return state.merge(IMap({
                searchResults: {
                    data: [...state.get('searchResults')?.data, ...action?.payload?.data],
                    metadata: action?.payload?.metadata,
                },
            }));
        case ClapiAction.GET_PROPERTY_DETAILS_BY_IDS:
            return state.merge(IMap({
                isFetchingAdditionalDetails: true,
            }));
        case ClapiAction.GET_PROPERTY_DETAILS_BY_IDS_SUCCESS:
            return state.merge(IMap({
                isFetchingAdditionalDetails: false,
                propertySummaries: state.get('propertySummaries').concat(action.payload.propertySummaryList),
            }));
        case ClapiAction.GET_PROPERTY_DETAILS_BY_IDS_FAIL:
            return state.merge(IMap({
                isFetchingAdditionalDetails: false,
                propertySummaries: state.get('propertySummaries').concat(action.payload),
            }));
        case ClapiAction.CLEAR_SEARCH_RESULTS_PROPERTY_SUMMARIES:
            return state.merge(IMap({
                propertySummaries: [],
            }));
        case ClapiAction.SET_SEARCH_STATUS_TYPE: {
            return state.merge(IMap({
                statusType: action.payload,
                searchResults: null, // for smooth transition
                retainedSearchResults: null,
                pagingSourceOffsets: null,
                displayControl: 'all',
            }));
        }
        case ClapiAction.UPDATE_SAVED_LIST_FILTERS_LIMIT:
        case ClapiAction.UPDATE_SEARCH_FILTERS_LIMIT: {
            return state.merge(IMap({
                limit: action.payload,
                // The `pageLimitOffset` resets the offset to 0.
                // It should be used on Per Page Limit dropdown only.
                pageLimitOffset: '0',
            }));
        }
        case ClapiAction.CLEAR_PAGE_LIMIT_OFFSET: {
            return state.merge(IMap({
                pageLimitOffset: null,
            }));
        }
        case ClapiAction.SET_ACTIVE_VIEW: {
            return state.merge(IMap({
                activeView: action.payload,
            }));
        }
        case ClapiAction.SET_DISPLAY_CONTROL: {
            return state.merge(IMap({
                displayControl: action.payload,
            }));
        }
        case SET_SELECTED_PROPERTY: {
            return state.merge(IMap({
                map: IMap({
                    selectedProperty: action.payload, // identifies which property from search result is selected
                    dynamic: {
                        ...state.get('map').get('dynamic'),
                        coordinates: null,
                    },
                }),
            }))
        }
        case SET_SELECTED_PROPERTY_FROM_DYNAMIC_BUBBLE: {
            const propertiesKey = AddressHelper
                .excludeUnit(action.payload[0].addressComplete);
            const current = state.get('map').get('dynamic')?.selectedProperty;

            if (propertiesKey in current) return state;

            return state.merge(IMap({
                map: IMap({
                    selectedProperty: state.get('map').get('selectedProperty'),
                    dynamic: {
                        properties: [],
                        selectedProperty: {
                            ...current,
                            [propertiesKey]: action.payload,
                        },
                    },
                }),
            }))
        }
        case SET_DYNAMIC_COORDINATES: {
            return state.merge(IMap({
                map: IMap({
                    selectedProperty: state.get('map').get('selectedProperty'),
                    dynamic: {
                        ...state.get('map').get('dynamic'),
                        coordinates: action.payload,
                        properties: [],
                    }
                })
            }));
        }
        case GET_PROPERTY_BY_COORDINATES_SUCCESS: {
            return state.merge(IMap({
                map: IMap({
                    selectedProperty: state.get('map').get('selectedProperty'),
                    dynamic: {
                        ...state.get('map').get('dynamic'),
                        properties: action.payload,
                    }
                })
            }));
        }
        case GET_PROPERTY_BY_COORDINATES_FAILED: {
            return state.merge(IMap({
                map: IMap({
                    selectedProperty: state.get('map').get('selectedProperty'),
                    dynamic: {
                        ...state.get('map').get('dynamic'),
                        properties: [{ error: true }],
                    }
                })
            }));
        }
        case HANDLE_DYNAMIC_PROPERTY_SELECTED: {
            const selectedProperties = state.get('map').get('dynamic').selectedProperty;
            return state.merge(IMap({
                map: IMap({
                    selectedProperty: state.get('map').get('selectedProperty'),
                    dynamic: {
                        ...state.get('map').get('dynamic'),
                        selectedProperty: retainSelectedByPayload(selectedProperties, action.payload),
                    }
                })
            }));
        }
        default:
            return state;
    }
}
